/* ============================================================
   TransferLabs — DTF Builder (multi-design)

   Modules:
     1. Config & state
     2. Helpers (money, bytes, clamp, round)
     3. Cart fetch (existing DTF qty)
     4. Variant bucket selection
     5. Tier logic (cumulative)
     6. Design lifecycle (create / remove / switch active)
     7. File handling (per-design)
     8. Preview (active design)
     9. Size & qty inputs (active design, aspect lock)
    10. Validation
    11. Recalc + paint
    12. Designs strip render
    13. Submit (sequential XHR per design)
   ============================================================ */

(function () {
  'use strict';

  function initBuilder(root) {
    if (!root || root.dataset.tlDtfReady === '1') return;
    root.dataset.tlDtfReady = '1';

    /* 1 ─── Config ─────────────────────────────────────────── */
    const PPI         = parseFloat(root.dataset.pricePerSqin) || 0.08;
    const MIN_PRICE   = parseFloat(root.dataset.minPrice)     || 2.99;
    const TIERS       = JSON.parse(root.dataset.tiers || '[]').sort((a, b) => a.qty - b.qty);
    const CURRENCY    = root.dataset.currency || 'USD';
    const DPI         = parseFloat(root.dataset.dpi) || 300;
    const MAX_SQIN    = parseFloat(root.dataset.maxSqin) || 400;
    const MIN_DIM     = 1;
    const MAX_DIM     = 22;
    const PRODUCT_ID  = parseInt(root.dataset.productId, 10) || 0;
    const CART_ADD    = root.dataset.cartAddUrl || '/cart/add';
    const CART_URL    = root.dataset.cartUrl    || '/cart';
    const CART_JS_URL = root.dataset.cartJsUrl  || '/cart.js';
    const DEFAULT_W   = parseFloat(root.dataset.defaultW) || 11;
    const DEFAULT_H   = parseFloat(root.dataset.defaultH) || 14;
    const MAX_DESIGNS = 10;

    let variants = [];
    try {
      variants = JSON.parse(root.querySelector('[data-tl-dtf-variants]')?.textContent || '[]')
        .filter(v => Number.isFinite(v.sqin) && v.available)
        .sort((a, b) => a.sqin - b.sqin);
    } catch (e) { console.warn('[tl-dtf] variant parse failed', e); }
    const MAX_BUCKET_SQIN = variants.length ? variants[variants.length - 1].sqin : MAX_SQIN;

    /* DOM refs */
    const $designs       = root.querySelector('[data-tl-dtf-designs]');
    const $designCounter = root.querySelector('[data-tl-dtf-design-counter]');
    const $fileInput     = root.querySelector('[data-tl-dtf-file]');
    const $drop          = root.querySelector('[data-tl-dtf-drop]');
    const $upEmpty       = root.querySelector('[data-tl-dtf-upload-empty]');
    const $upFilled      = root.querySelector('[data-tl-dtf-upload-filled]');
    const $thumbImg      = root.querySelector('[data-tl-dtf-file-thumb-img]');
    const $thumbBadge    = root.querySelector('[data-tl-dtf-file-badge]');
    const $fileName      = root.querySelector('[data-tl-dtf-filename]');
    const $fileSize      = root.querySelector('[data-tl-dtf-filesize]');
    const $remove        = root.querySelector('[data-tl-dtf-remove]');
    const $replace       = root.querySelector('[data-tl-dtf-replace]');

    const $previewWrap   = root.querySelector('[data-tl-dtf-preview-wrap]');
    const $priceStrip    = root.querySelector('[data-tl-dtf-price-strip]');
    const $eachNow       = root.querySelector('[data-tl-dtf-each-now]');
    const $eachWas       = root.querySelector('[data-tl-dtf-each-was]');
    const $eachPct       = root.querySelector('[data-tl-dtf-each-pct]');
    const $savingsCell   = root.querySelector('[data-tl-dtf-savings-cell]');
    const $designSavings = root.querySelector('[data-tl-dtf-design-savings]');
    const $img           = root.querySelector('[data-tl-dtf-img]');
    const $frame         = root.querySelector('[data-tl-dtf-frame]');
    const $detected      = root.querySelector('[data-tl-dtf-detected]');
    const $detectedPx    = root.querySelector('[data-tl-dtf-detected-px]');
    const $detectedInches= root.querySelector('[data-tl-dtf-detected-inches]');
    const $measureW      = root.querySelector('[data-tl-dtf-measure-w]');
    const $measureH      = root.querySelector('[data-tl-dtf-measure-h]');

    const $w             = root.querySelector('[data-tl-dtf-w]');
    const $h             = root.querySelector('[data-tl-dtf-h]');
    const $q             = root.querySelector('[data-tl-dtf-q]');
    const $lock          = root.querySelector('[data-tl-dtf-lock]');

    const $runningQty    = root.querySelector('[data-tl-dtf-running-qty]');
    const $runningPct    = root.querySelector('[data-tl-dtf-running-pct]');
    const $cartStat      = root.querySelector('[data-tl-dtf-cart-stat]');
    const $cartQtyOut    = root.querySelector('[data-tl-dtf-cart-qty]');
    const $designQtyOut  = root.querySelector('[data-tl-dtf-design-qty]');
    const $bulkOp        = root.querySelector('[data-tl-dtf-bulk-op]');
    const $bulkEq        = root.querySelector('[data-tl-dtf-bulk-eq]');
    const $totalStat     = root.querySelector('[data-tl-dtf-total-stat]');
    const $nextHint      = root.querySelector('[data-tl-dtf-next-hint]');
    const $nextNeed      = root.querySelector('[data-tl-dtf-next-need]');
    const $nextPct       = root.querySelector('[data-tl-dtf-next-pct]');

    const $lineItems     = root.querySelector('[data-tl-dtf-line-items]');
    const $subtotal      = root.querySelector('[data-tl-dtf-subtotal]');
    const $discRow       = root.querySelector('[data-tl-dtf-discount-row]');
    const $discLabel     = root.querySelector('[data-tl-dtf-discount-label]');
    const $disc          = root.querySelector('[data-tl-dtf-discount]');
    const $total         = root.querySelector('[data-tl-dtf-total]');

    const $atc           = root.querySelector('[data-tl-dtf-submit]');
    const $atcLabel      = root.querySelector('[data-tl-dtf-atc-label]');
    const $err           = root.querySelector('[data-tl-dtf-error]');
    const $sizeErr       = root.querySelector('[data-tl-dtf-size-err]');
    const $tiers         = root.querySelectorAll('[data-tl-tier]');
    const $thumbs        = root.querySelectorAll('[data-tl-dtf-thumbs] [data-src]');
    const $heroImg       = root.querySelector('.tl-dtf__hero-img');

    /* State */
    const state = {
      designs: [],
      activeId: null,
      existingCartQty: 0,
      nextDesignId: 1,
    };

    /* 2 ─── Helpers ────────────────────────────────────────── */
    const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: CURRENCY, minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const fmtBytes = (bytes) => bytes < 1024 ? bytes + ' B'
                            : bytes < 1024*1024 ? (bytes/1024).toFixed(1) + ' KB'
                            : (bytes/1024/1024).toFixed(2) + ' MB';
    const round2 = (n) => Math.round(n * 100) / 100;
    const clamp  = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
    const escapeHtml = (s) => String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));

    /* 3 ─── Cart fetch ─────────────────────────────────────── */
    function fetchExistingCartQty() {
      if (!PRODUCT_ID) return Promise.resolve(0);
      return fetch(CART_JS_URL, { headers: { 'Accept': 'application/json' } })
        .then(r => r.ok ? r.json() : { items: [] })
        .then(cart => {
          const items = cart.items || [];
          return items
            .filter(it => it.product_id === PRODUCT_ID)
            .reduce((sum, it) => sum + (it.quantity || 0), 0);
        })
        .catch(() => 0);
    }

    /* 4 ─── Variant bucket ─────────────────────────────────── */
    function pickVariant(needSqin) {
      for (let i = 0; i < variants.length; i++) {
        if (variants[i].sqin >= needSqin) return variants[i];
      }
      return null;
    }

    /* 5 ─── Tier logic (cumulative) ────────────────────────── */
    function tierFor(qty) {
      let active = null;
      for (const t of TIERS) if (qty >= t.qty) active = t;
      return active;
    }

    /* 6 ─── Design lifecycle ───────────────────────────────── */
    function createDesign() {
      return {
        id: String(state.nextDesignId++),
        file: null,
        fileName: '',
        fileSize: 0,
        fileURL: null,
        fileKind: null,
        detectedPx: '',
        detectedInches: '',
        w: DEFAULT_W,
        h: DEFAULT_H,
        q: 1,
        ratio: DEFAULT_W / DEFAULT_H,
        sizeValid: true,
        hasInteracted: false,
      };
    }

    function getActive() {
      return state.designs.find(d => d.id === state.activeId);
    }

    function addDesign() {
      if (state.designs.length >= MAX_DESIGNS) return;
      const d = createDesign();
      state.designs.push(d);
      setActive(d.id);
    }

    function removeDesign(id) {
      const idx = state.designs.findIndex(d => d.id === id);
      if (idx < 0) return;
      const d = state.designs[idx];
      if (d.fileURL) try { URL.revokeObjectURL(d.fileURL); } catch (e) {}
      state.designs.splice(idx, 1);
      if (state.designs.length === 0) {
        const fresh = createDesign();
        fresh.q = 0;
        state.designs.push(fresh);
        state.activeId = fresh.id;
      } else if (state.activeId === id) {
        state.activeId = state.designs[Math.max(0, idx - 1)].id;
      }
      clearError();
      clearSizeErr();
      paintAll();
    }

    function setActive(id) {
      if (!state.designs.find(d => d.id === id)) return;
      state.activeId = id;
      paintAll();
    }

    /* 7 ─── File handling ──────────────────────────────────── */
    function fileKind(file) {
      const t = file.type || '';
      const name = (file.name || '').toLowerCase();
      if (/^image\/(png|jpe?g)/.test(t) || /\.(png|jpe?g)$/.test(name)) return 'raster';
      if (/svg/.test(t) || /\.svg$/.test(name)) return 'svg';
      if (/pdf/.test(t) || /\.pdf$/.test(name)) return 'pdf';
      return 'file';
    }

    function attachFileToActive(file) {
      const d = getActive();
      if (!d || !file) return;

      const MAX_MB = 20;
      if (file.size > MAX_MB * 1024 * 1024) {
        showError(`File is ${fmtBytes(file.size)}. Shopify caps uploads at ${MAX_MB}MB.`);
      } else {
        clearError();
      }

      // Revoke previous URL if replacing
      if (d.fileURL) try { URL.revokeObjectURL(d.fileURL); } catch (e) {}

      d.file = file;
      d.fileName = file.name;
      d.fileSize = file.size;
      d.fileKind = fileKind(file);
      d.fileURL = (d.fileKind === 'raster' || d.fileKind === 'svg') ? URL.createObjectURL(file) : null;
      d.hasInteracted = true;

      if ($drop) {
        $drop.classList.add('is-success');
        setTimeout(() => $drop.classList.remove('is-success'), 600);
      }

      // Detect pixel dimensions for raster → auto-scale via DPI
      if (d.fileKind === 'raster' && d.fileURL) {
        const probe = new Image();
        probe.onload = () => {
          if (probe.naturalWidth && probe.naturalHeight) {
            d.detectedPx = `${probe.naturalWidth} × ${probe.naturalHeight} px`;
            const ratio = probe.naturalWidth / probe.naturalHeight;
            const wIn = round2(probe.naturalWidth / DPI);
            const hIn = round2(probe.naturalHeight / DPI);
            let finalW = clamp(wIn, MIN_DIM, MAX_DIM);
            let finalH = clamp(hIn, MIN_DIM, MAX_DIM);
            if (finalW !== wIn) finalH = round2(finalW / ratio);
            if (finalH !== hIn) finalW = round2(finalH * ratio);
            d.w = clamp(round2(finalW), MIN_DIM, MAX_DIM);
            d.h = clamp(round2(finalH), MIN_DIM, MAX_DIM);
            d.ratio = ratio;
            d.detectedInches = `· Auto-scaled to ${d.w}" × ${d.h}"`;
            // Only repaint active editor if this design is still the active one
            if (getActive()?.id === d.id) paintActiveEditor();
            paintSummary();
          }
        };
        probe.src = d.fileURL;
      } else {
        d.detectedPx = '';
        d.detectedInches = '';
      }

      paintAll();
    }

    /* 8/9/10 ─── input handlers ────────────────────────────── */
    function clampInput(input, value, min, max) {
      let v = parseFloat(value);
      if (!Number.isFinite(v)) v = min;
      v = clamp(v, min, max);
      input.value = v;
      return v;
    }

    function onWidthChange() {
      const d = getActive(); if (!d) return;
      d.hasInteracted = true;
      const w = clampInput($w, $w.value, MIN_DIM, MAX_DIM);
      d.w = w;
      if ($lock?.checked) {
        const h = round2(clamp(w / d.ratio, MIN_DIM, MAX_DIM));
        $h.value = h; d.h = h;
      } else {
        d.ratio = d.w / d.h;
      }
      paintAll();
    }
    function onHeightChange() {
      const d = getActive(); if (!d) return;
      d.hasInteracted = true;
      const h = clampInput($h, $h.value, MIN_DIM, MAX_DIM);
      d.h = h;
      if ($lock?.checked) {
        const w = round2(clamp(h * d.ratio, MIN_DIM, MAX_DIM));
        $w.value = w; d.w = w;
      } else {
        d.ratio = d.w / d.h;
      }
      paintAll();
    }
    function onQtyChange() {
      const d = getActive(); if (!d) return;
      d.q = clampInput($q, $q.value, 1, 99999);
      paintAll();
    }

    /* 11 ─── Paint ─────────────────────────────────────────── */
    function showError(msg)   { if ($err) { $err.textContent = msg; $err.hidden = false; } }
    function clearError()     { if ($err) { $err.hidden = true; $err.textContent = ''; } }
    function showSizeErr(msg) { if ($sizeErr) { $sizeErr.textContent = msg; $sizeErr.hidden = false; } }
    function clearSizeErr()   { if ($sizeErr) { $sizeErr.hidden = true; $sizeErr.textContent = ''; } }

    function showPreviewWrap() {
      if (!$previewWrap || $previewWrap.classList.contains('is-shown')) return;
      $previewWrap.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => $previewWrap.classList.add('is-shown')));
    }
    function hidePreviewWrap() {
      if (!$previewWrap) return;
      $previewWrap.classList.remove('is-shown');
      $previewWrap.hidden = true;
    }

    const $form = root.querySelector('.tl-dtf__form');

    function paintActiveEditor() {
      const d = getActive(); if (!d) return;

      // Toggle 2-col grid layout when active design has a file
      if ($form) $form.classList.toggle('tl-dtf__form--has-preview', !!d.file);

      // Upload UI
      if (d.file) {
        $upEmpty.hidden = true;
        $upFilled.hidden = false;
        $fileName.textContent = d.fileName;
        $fileSize.textContent = fmtBytes(d.fileSize);
        if (d.fileKind === 'raster' && d.fileURL) {
          $thumbImg.src = d.fileURL;
          $thumbImg.hidden = false;
          $thumbBadge.hidden = true;
        } else {
          $thumbImg.hidden = true;
          $thumbImg.removeAttribute('src');
          $thumbBadge.hidden = false;
          $thumbBadge.textContent = d.fileKind === 'svg' ? 'SVG' : d.fileKind === 'pdf' ? 'PDF' : 'FILE';
        }
      } else {
        $upEmpty.hidden = false;
        $upFilled.hidden = true;
      }

      // Preview
      if (d.file) {
        if (d.fileURL && (d.fileKind === 'raster' || d.fileKind === 'svg')) {
          $img.src = d.fileURL;
          $img.hidden = false;
        } else {
          $img.hidden = true;
          $img.removeAttribute('src');
        }
        showPreviewWrap();
        if (d.detectedPx) {
          $detectedPx.textContent = d.detectedPx;
          $detectedInches.textContent = d.detectedInches || '';
          $detected.hidden = false;
        } else {
          $detected.hidden = true;
        }
      } else {
        hidePreviewWrap();
        $img.hidden = true;
        $img.removeAttribute('src');
        $detected.hidden = true;
      }

      // Size + qty inputs
      $w.value = d.w;
      $h.value = d.h;
      $q.value = d.q;

      // Preview frame aspect
      if ($frame) {
        $frame.style.aspectRatio = `${d.w} / ${d.h}`;
        if (d.w >= d.h) { $frame.style.width = '85%'; $frame.style.height = 'auto'; }
        else            { $frame.style.height = '85%'; $frame.style.width = 'auto'; }
      }
      if ($measureW) $measureW.textContent = `${round2(d.w).toFixed(2)}"`;
      if ($measureH) $measureH.textContent = `${round2(d.h).toFixed(2)}"`;

      // Validate active design's size (gated by hasInteracted)
      const userSqIn = d.w * d.h;
      const needSqIn = Math.max(1, Math.ceil(userSqIn));
      let sizeValid = true;
      if (d.hasInteracted) {
        if (userSqIn < 1) { sizeValid = false; showSizeErr(`Size too small. Minimum is 1 sq in.`); }
        else if (userSqIn > MAX_SQIN || needSqIn > MAX_BUCKET_SQIN) {
          sizeValid = false;
          showSizeErr(`Size out of range. Max ${MAX_BUCKET_SQIN} sq in. Reduce width or height.`);
        } else clearSizeErr();
      } else clearSizeErr();
      d.sizeValid = sizeValid;
    }

    function computeDesignPricing(d) {
      const userSqIn = d.w * d.h;
      const needSqIn = Math.max(1, Math.ceil(userSqIn));
      const variant  = d.sizeValid ? pickVariant(needSqIn) : null;
      const unit     = variant ? variant.price / 100 : Math.max(round2(userSqIn * PPI), MIN_PRICE);
      return { userSqIn, variant, unit };
    }

    function paintSummary() {
      // Cumulative qty across designs (don't add cart here yet — cart is shown separately)
      const designsQty = state.designs.reduce((s, d) => s + (d.q || 0), 0);
      const cumulativeQty = designsQty + state.existingCartQty;

      const tier = tierFor(cumulativeQty);
      const tierPct = tier ? tier.pct : 0;

      // Per-design line items
      let subtotal = 0;
      let allValid = true;
      let hasFiles = state.designs.every(d => !!d.file);

      const lineHtml = state.designs.map((d, i) => {
        const p = computeDesignPricing(d);
        if (!p.variant) allValid = false;
        // No artwork yet → don't charge for the placeholder size.
        // The ATC button is already gated on `hasFiles`, so this is
        // purely how the running totals appear before upload.
        const hasFile = !!d.file;
        const unit = hasFile ? p.unit : 0;
        const lineTotal = unit * d.q;
        subtotal += lineTotal;
        const wTxt = round2(d.w).toFixed(2);
        const hTxt = round2(d.h).toFixed(2);
        const thumb = d.fileURL && d.fileKind === 'raster'
          ? `<img class="tl-dtf__line-thumb" src="${escapeHtml(d.fileURL)}" alt="">`
          : `<span class="tl-dtf__line-thumb tl-dtf__line-thumb--badge">${d.fileKind ? escapeHtml(d.fileKind.toUpperCase()) : '—'}</span>`;
        return `
          <li class="tl-dtf__line">
            ${thumb}
            <div class="tl-dtf__line-text">
              <strong>Design ${i + 1}</strong>
              <span>${wTxt}" × ${hTxt}" · ${d.q} × ${fmt.format(unit)}</span>
            </div>
            <div class="tl-dtf__line-amount">${fmt.format(lineTotal)}</div>
          </li>
        `;
      }).join('');

      $lineItems.innerHTML = lineHtml;

      const discountAmt = subtotal * (tierPct / 100);
      const total = subtotal - discountAmt;

      $subtotal.textContent = fmt.format(subtotal);
      $total.textContent = fmt.format(total);
      if (tierPct > 0) {
        $discLabel.textContent = `Volume discount (${tierPct}% off)`;
        $disc.textContent = `−${fmt.format(discountAmt)}`;
        $discRow.hidden = false;
      } else {
        $discRow.hidden = true;
      }

      // Bulk card breakdown: [cart] + [this order] = [cumulative → pct]
      const hasCart = state.existingCartQty > 0;
      $runningQty.textContent = String(cumulativeQty);
      if ($designQtyOut) $designQtyOut.textContent = String(designsQty);
      if ($cartQtyOut)   $cartQtyOut.textContent   = String(state.existingCartQty);
      if ($cartStat)     $cartStat.hidden = !hasCart;
      if ($bulkOp)       $bulkOp.hidden   = !hasCart;
      if ($bulkEq)       $bulkEq.hidden   = !hasCart;
      if ($totalStat)    $totalStat.hidden = !hasCart;
      if ($runningPct) {
        if (tierPct > 0) {
          $runningPct.textContent = `→ ${tierPct}% off`;
          $runningPct.hidden = false;
        } else {
          $runningPct.hidden = true;
        }
      }

      // Next-tier hint: how many more items to unlock the next discount
      const nextTier = TIERS.find(t => cumulativeQty < t.qty);
      if ($nextHint) {
        if (nextTier) {
          const need = nextTier.qty - cumulativeQty;
          if ($nextNeed) $nextNeed.textContent = String(need);
          if ($nextPct)  $nextPct.textContent  = `${nextTier.pct}%`;
          $nextHint.hidden = false;
        } else {
          $nextHint.hidden = true;
        }
      }

      // Tier highlight
      $tiers.forEach(li => li.classList.remove('is-active'));
      const tierIdx = tier ? TIERS.indexOf(tier) + 1 : 0;
      const activeLi = root.querySelector(`[data-tl-tier="${tierIdx}"]`);
      if (activeLi) activeLi.classList.add('is-active');

      // Per-each price strip — reflects active design at the cumulative tier
      if ($priceStrip) {
        const active = getActive();
        const activeP = active ? computeDesignPricing(active) : null;
        if (active && active.file && active.sizeValid && activeP && activeP.variant) {
          const unit = activeP.unit;
          const unitAfter = unit * (1 - tierPct / 100);
          const designSavings = (unit - unitAfter) * (active.q || 0);
          $priceStrip.hidden = false;
          $eachNow.textContent = fmt.format(unitAfter);
          if (tierPct > 0) {
            $eachWas.textContent = fmt.format(unit);
            $eachWas.hidden = false;
            $eachPct.textContent = `${tierPct}% off`;
            $eachPct.hidden = false;
            $savingsCell.hidden = false;
            $designSavings.textContent = fmt.format(designSavings);
          } else {
            $eachWas.hidden = true;
            $eachPct.hidden = true;
            $savingsCell.hidden = true;
          }
        } else {
          $priceStrip.hidden = true;
        }
      }

      // ATC button state
      const allSizesValid = state.designs.every(d => d.sizeValid);
      const canAdd = hasFiles && allSizesValid && allValid && state.designs.length > 0;
      $atc.disabled = !canAdd;
      if (canAdd) {
        $atcLabel.textContent = state.designs.length === 1
          ? `Add to Cart — ${fmt.format(total)}`
          : `Add ${state.designs.length} designs — ${fmt.format(total)}`;
      } else if (!hasFiles) {
        $atcLabel.textContent = `Upload artwork on every design`;
      } else if (!allSizesValid) {
        $atcLabel.textContent = `Adjust sizes to continue`;
      } else {
        $atcLabel.textContent = `Add to Cart`;
      }
    }

    function paintAll() {
      paintActiveEditor();
      renderDesignsStrip();
      paintSummary();
      if ($designCounter) {
        const n = state.designs.length;
        $designCounter.textContent = n === 1 ? '1 design' : `${n} designs`;
      }
    }

    /* 12 ─── Designs strip render ──────────────────────────── */
    function renderDesignsStrip() {
      if (!$designs) return;
      const html = state.designs.map((d, i) => {
        const isActive = d.id === state.activeId;
        const thumb = d.fileURL && d.fileKind === 'raster'
          ? `<img class="tl-dtf__chip-thumb" src="${escapeHtml(d.fileURL)}" alt="">`
          : d.file
            ? `<span class="tl-dtf__chip-thumb tl-dtf__chip-thumb--badge">${escapeHtml(d.fileKind ? d.fileKind.toUpperCase().slice(0,3) : 'F')}</span>`
            : `<span class="tl-dtf__chip-thumb tl-dtf__chip-thumb--empty">${i + 1}</span>`;
        const removeBtn = `<button class="tl-dtf__chip-x" data-tl-dtf-design-remove="${escapeHtml(d.id)}" aria-label="Remove design ${i+1}" type="button">×</button>`;
        return `
          <div class="tl-dtf__chip ${isActive ? 'is-active' : ''}" data-tl-dtf-design-switch="${escapeHtml(d.id)}" role="button" tabindex="0">
            ${thumb}
            <span class="tl-dtf__chip-label">Design ${i + 1}</span>
            ${d.file ? '' : '<span class="tl-dtf__chip-badge">empty</span>'}
            ${removeBtn}
          </div>
        `;
      }).join('');

      const addBtn = state.designs.length < MAX_DESIGNS
        ? `<button type="button" class="tl-dtf__chip-add" data-tl-dtf-design-add aria-label="Add another design">
             <span aria-hidden="true">+</span> Add design
           </button>`
        : '';

      $designs.innerHTML = html + addBtn;
    }

    /* Event delegation on designs strip */
    $designs?.addEventListener('click', (e) => {
      const t = e.target;
      const removeBtn = t.closest('[data-tl-dtf-design-remove]');
      if (removeBtn) {
        e.preventDefault(); e.stopPropagation();
        removeDesign(removeBtn.getAttribute('data-tl-dtf-design-remove'));
        return;
      }
      const addBtn = t.closest('[data-tl-dtf-design-add]');
      if (addBtn) {
        e.preventDefault();
        addDesign();
        return;
      }
      const switchBtn = t.closest('[data-tl-dtf-design-switch]');
      if (switchBtn) {
        setActive(switchBtn.getAttribute('data-tl-dtf-design-switch'));
      }
    });

    // Chip is a div[role=button], so wire Enter/Space ourselves.
    $designs?.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const switchBtn = e.target.closest('[data-tl-dtf-design-switch]');
      if (!switchBtn) return;
      e.preventDefault();
      setActive(switchBtn.getAttribute('data-tl-dtf-design-switch'));
    });

    /* File / replace / remove (act on active design) */
    $fileInput?.addEventListener('change', (e) => {
      const file = e.target.files?.[0];
      if (file) attachFileToActive(file);
      // reset input so re-selecting the same filename triggers change
      e.target.value = '';
    });

    $remove?.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      const d = getActive(); if (!d) return;
      if (d.fileURL) try { URL.revokeObjectURL(d.fileURL); } catch (err) {}
      d.file = null; d.fileName = ''; d.fileSize = 0; d.fileURL = null;
      d.fileKind = null; d.detectedPx = ''; d.detectedInches = '';
      d.w = DEFAULT_W;
      d.h = DEFAULT_H;
      d.ratio = DEFAULT_W / DEFAULT_H;
      d.q = 0;
      d.sizeValid = true;
      d.hasInteracted = false;
      clearError();
      clearSizeErr();
      paintAll();
    });

    $replace?.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      $fileInput.click();
    });

    /* Drag & drop */
    ['dragenter','dragover'].forEach(ev =>
      $drop?.addEventListener(ev, (e) => { e.preventDefault(); e.stopPropagation(); $drop.classList.add('is-dragging'); })
    );
    ['dragleave','drop'].forEach(ev =>
      $drop?.addEventListener(ev, (e) => { e.preventDefault(); e.stopPropagation(); $drop.classList.remove('is-dragging'); })
    );
    $drop?.addEventListener('drop', (e) => {
      const file = e.dataTransfer?.files?.[0];
      if (file) attachFileToActive(file);
    });

    /* Size + qty */
    $w?.addEventListener('input', onWidthChange);
    $h?.addEventListener('input', onHeightChange);
    $q?.addEventListener('input', onQtyChange);
    root.querySelectorAll('[data-tl-dtf-step]').forEach(btn => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.tlDtfStep;
        const dir = parseFloat(btn.dataset.dir) || 1;
        if (target === 'w') { $w.value = round2(parseFloat($w.value || 0) + dir * 0.5); onWidthChange(); }
        else if (target === 'h') { $h.value = round2(parseFloat($h.value || 0) + dir * 0.5); onHeightChange(); }
        else if (target === 'q') { $q.value = Math.max(1, parseInt($q.value || '1', 10) + dir); onQtyChange(); }
      });
    });
    $lock?.addEventListener('change', () => {
      const d = getActive();
      if (d && $lock.checked) d.ratio = d.w / d.h;
    });

    /* Gallery thumbs */
    $thumbs.forEach(t => {
      t.addEventListener('click', () => {
        if (t.dataset.src && $heroImg) $heroImg.src = t.dataset.src;
        $thumbs.forEach(o => o.classList.remove('is-active'));
        t.classList.add('is-active');
      });
    });

    /* 13 ─── Submit (sequential XHR per design) ────────────── */
    function setAtcProgress(pct, label) {
      $atc.style.setProperty('--tl-progress', `${pct}%`);
      $atcLabel.textContent = label;
    }

    function submitDesign(d, designIdx, totalDesigns) {
      return new Promise((resolve, reject) => {
        const p = computeDesignPricing(d);
        if (!p.variant) return reject(new Error(`Design ${designIdx + 1}: no matching size tier`));

        const qtyTotal = state.designs.reduce((s, x) => s + x.q, 0) + state.existingCartQty;
        const tier = tierFor(qtyTotal);
        const tierPct = tier ? tier.pct : 0;
        const wTxt = String(round2(d.w));
        const hTxt = String(round2(d.h));
        const sqInTxt = String(round2(d.w * d.h));
        const unitDiscounted = p.unit * (1 - tierPct / 100);

        // Properties prefixed with `_` are hidden from customer-facing
        // cart / checkout / email per Shopify convention. Admin & order
        // detail still see them — useful for production/fulfillment.
        const fd = new FormData();
        fd.append('id', String(p.variant.id));
        fd.append('quantity', String(d.q));
        fd.append('properties[Artwork]',           d.file, d.fileName);
        fd.append('properties[Filename]',          d.fileName);
        fd.append('properties[Size]',              `${wTxt}" × ${hTxt}"`);
        fd.append('properties[_Square Inches]',    sqInTxt);
        fd.append('properties[_Bucket]',           `${p.variant.sqin} sq in tier`);
        fd.append('properties[_Unit Price]',       fmt.format(p.unit));
        fd.append('properties[_Tier At Add]',      tierPct > 0 ? `${tierPct}% off (cumulative qty ${qtyTotal})` : 'Base price');
        fd.append('properties[_Unit After Tier]',  fmt.format(unitDiscounted));

        const xhr = new XMLHttpRequest();
        xhr.open('POST', CART_ADD, true);
        xhr.setRequestHeader('Accept', 'application/javascript');

        xhr.upload.addEventListener('progress', (ev) => {
          if (ev.lengthComputable) {
            const pct = (ev.loaded / ev.total) * 100;
            const overall = ((designIdx + pct / 100) / totalDesigns) * 100;
            setAtcProgress(overall, `Uploading design ${designIdx + 1} of ${totalDesigns}… ${Math.round(pct)}%`);
          }
        });

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 400) {
            const overall = ((designIdx + 1) / totalDesigns) * 100;
            setAtcProgress(overall, `Added design ${designIdx + 1} of ${totalDesigns}`);
            resolve();
          } else {
            reject(new Error(`Server returned ${xhr.status} for design ${designIdx + 1}`));
          }
        };
        xhr.onerror = () => reject(new Error(`Network error on design ${designIdx + 1}`));
        xhr.send(fd);
      });
    }

    async function submitAll() {
      // Validate
      const designs = state.designs;
      if (designs.length === 0) { showError('Add at least one design.'); return; }
      for (let i = 0; i < designs.length; i++) {
        const d = designs[i];
        if (!d.file)        { showError(`Design ${i + 1}: please upload artwork.`); setActive(d.id); return; }
        if (!d.sizeValid)   { showError(`Design ${i + 1}: invalid size.`); setActive(d.id); return; }
      }

      $atc.classList.add('is-uploading');
      $atc.disabled = true;
      clearError();
      setAtcProgress(0, `Uploading design 1 of ${designs.length}…`);

      try {
        for (let i = 0; i < designs.length; i++) {
          await submitDesign(designs[i], i, designs.length);
        }
        setAtcProgress(100, `Redirecting to cart…`);
        window.location.href = CART_URL;
      } catch (err) {
        $atc.classList.remove('is-uploading');
        $atc.disabled = false;
        $atcLabel.textContent = 'Add to Cart';
        showError(err.message + '. Please try again or email art@transferlabs.com.');
      }
    }

    $atc?.addEventListener('click', submitAll);

    /* Cart refresh — keep cumulative tier accurate when cart changes
       elsewhere (drawer add/remove, qty edit) or after returning to tab */
    function refreshCartQty() {
      return fetchExistingCartQty().then(qty => {
        if (qty !== state.existingCartQty) {
          state.existingCartQty = qty;
          paintAll();
        }
      });
    }

    document.addEventListener('cart:update', refreshCartQty);
    document.addEventListener('cart:refresh', refreshCartQty);
    document.addEventListener('cart:change', refreshCartQty);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') refreshCartQty();
    });
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) refreshCartQty();
    });

    /* ─── Init ──────────────────────────────────────────────── */
    state.designs.push(createDesign());
    state.activeId = state.designs[0].id;

    refreshCartQty();
    paintAll();
  }

  function init() { document.querySelectorAll('[data-tl-dtf]').forEach(initBuilder); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
